import React, { ErrorInfo, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import { withTranslation, WithTranslation } from "react-i18next";

interface Props extends WithTranslation {
    children: ReactNode;
}

interface State {
    hasError: boolean;
}

/**
 * Last line of defense: catches render errors anywhere in the tree and
 * shows a friendly message instead of a white screen / dev overlay.
 */
class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // eslint-disable-next-line no-console
        console.error("Uncaught render error:", error, errorInfo);
    }

    render(): ReactNode {
        const { hasError } = this.state;
        const { children, t } = this.props;

        if (hasError) {
            return (
                <Box sx={{
                    height: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 4,
                    textAlign: "center",
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {t("error_boundary.title", "Something went wrong")}
                    </Typography>
                    <Typography color="text.secondary">
                        {t("error_boundary.text", "An unexpected error occurred. Please reload the page.")}
                    </Typography>
                    <Button variant="contained" onClick={() => window.location.reload()}>
                        {t("error_boundary.reload", "Reload page")}
                    </Button>
                </Box>
            );
        }

        return children;
    }
}

export default withTranslation("common")(ErrorBoundary);
